// src/components/MyHubsFeed.jsx
"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import HubCard from "@/components/HubCard";
import CardList from "@/components/CardList";
import FeedSkeleton from "@/components/FeedSkeleton";
import { fetchActiveHubsForUser } from "@/lib/hubData";

export default function MyHubsFeed({
  user,
  pageSize = 25,
  allowedHubSlugs = null,
}) {
  const uid = user?.uid || null;
  const isAuthed = Boolean(uid);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!uid) {
      setRows([]);
      setError("");
      setLoading(false);
      return;
    }
    setRows(null);
    setError("");
    setLoading(true);
    fetchActiveHubsForUser(uid, pageSize, allowedHubSlugs)
      .then((hubs) => {
        if (cancelled) return;
        setRows(hubs);
        setError("");
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("MyHubsFeed error:", err);
        setError("Could not load your spaces.");
        setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid, pageSize, allowedHubSlugs]);

  if (!isAuthed) return <SignInPrompt />;
  if (loading && rows === null) return <FeedSkeleton />;
  if (error) return <ErrorBlock message={error} />;
  if (!rows || rows.length === 0) return <EmptyState portalScoped={Array.isArray(allowedHubSlugs)} />;

  return (
    <CardList>
      {rows.map((hub) => (
        <HubCard key={hub.id} hub={hub} />
      ))}
    </CardList>
  );
}

function SignInPrompt() {
  return (
    <div className="edge-mobile">
      <div className="card square-mobile text-center">
        <h4 className="font-semibold">My Spaces</h4>
        <p className="mt-1 text-sm text-[var(--muted)]">
          Sign in to see the spaces you’ve joined.
        </p>
        <Link
          href="/login"
          className="mt-3 inline-flex items-center justify-center rounded-[var(--radius)] bg-[var(--brand)] px-4 py-2 text-sm font-medium text-[var(--brand-foreground)]"
        >
          Sign in
        </Link>
      </div>
    </div>
  );
}

function EmptyState({ portalScoped = false }) {
  return (
    <div className="edge-mobile">
      <div className="card square-mobile text-center">
        <h4 className="font-semibold">My Spaces</h4>
        <p className="mt-1 text-sm text-[var(--muted)]">
          You haven’t joined any spaces yet.
        </p>
        {!portalScoped ? (
          <Link
            href="/new-hub"
            className="mt-3 inline-flex items-center justify-center rounded-[var(--radius)] border border-[var(--border)] px-4 py-2 text-sm font-medium text-[var(--fg-primary)] hover:bg-[var(--surface-2)]"
          >
            Create a space
          </Link>
        ) : null}
      </div>
    </div>
  );
}

function ErrorBlock({ message }) {
  return (
    <div className="edge-mobile">
      <div className="card square-mobile text-sm text-red-600">{message}</div>
    </div>
  );
}
